import React, { useState, useEffect } from 'react';
import { mockData } from '../mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Home, Flame, Calendar, Building2, Maximize2, Users, Droplet, Thermometer, Clock, AlertCircle, Wrench, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const HeatPumpRequest = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    buildingType: '',
    currentHeating: '',
    heatingAge: '',
    buildingYear: '',
    heatedArea: '',
    householdSize: '',
    waterHeating: '',
    heatingSystem: '',
    installationTime: '',
    heatingProblems: '',
    renovations: [],
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: ''
  });

  // Scroll nach oben beim Laden der Komponente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalSteps = 11; // Von 12 auf 11 reduziert (Step 11 entfernt)

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setTimeout(nextStep, 300);
  };

  const handleMultiSelect = (field, value) => {
    const current = formData[field] || [];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    setFormData({ ...formData, [field]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Sende Anfrage an Backend (speichert in DB und sendet E-Mail)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heat-pump-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Senden der Anfrage');
      }

      // Erfolg!
      toast({
        title: "✅ Anfrage erfolgreich gesendet!",
        description: "Vielen Dank für Ihre Anfrage. Wir werden uns in Kürze bei Ihnen melden.",
        duration: 5000,
      });
      setStep(totalSteps + 1);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "❌ Fehler beim Senden",
        description: "Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const OptionCard = ({ icon: Icon, label, value, onClick, selected }) => (
    <Card
      onClick={onClick}
      className={`cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl ${
        selected ? 'border-2 border-[#1A3A2E] bg-[#E8F5E0]' : 'border-2 border-transparent'
      }`}
    >
      <CardContent className="p-8 text-center">
        <Icon className="w-12 h-12 mx-auto mb-4 text-[#1A3A2E]" />
        <p className="font-semibold text-[#1A3A2E] break-words hyphens-auto" lang="de">{label}</p>
      </CardContent>
    </Card>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Für welchen Gebäudetyp benötigen Sie eine neue Heizung?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: 'einzelhaus', label: 'Einzelhaus', icon: Home },
                { value: 'doppelhaus', label: 'Doppelhaushälfte', icon: Home },
                { value: 'reihenendhaus', label: 'Reihenendhaus', icon: Home },
                { value: 'reihenmittelhaus', label: 'Reihenmittelhaus', icon: Home },
                { value: 'mehrfamilienhaus', label: 'Mehrfamilienhaus', icon: Building2 },
                { value: 'wohnung', label: 'Wohnung', icon: Home }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.buildingType === option.value}
                  onClick={() => handleSelect('buildingType', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wie heizen Sie aktuell?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: 'erdgas', label: 'Erdgas', icon: Flame },
                { value: 'fernwaerme', label: 'Nah-/Fernwärme', icon: Flame },
                { value: 'heizoel', label: 'Heizöl', icon: Droplet },
                { value: 'sonstiges', label: 'Sonstiges', icon: AlertCircle },
                { value: 'kein', label: 'Noch kein Brennstoff', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.currentHeating === option.value}
                  onClick={() => handleSelect('currentHeating', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wie viele Jahre ist Ihre Heizung schon in Betrieb?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: '30+', label: '30 Jahre und mehr', icon: Calendar },
                { value: '20-29', label: '20 - 29 Jahre', icon: Calendar },
                { value: '10-19', label: '10 - 19 Jahre', icon: Calendar },
                { value: '0-9', label: '0 - 9 Jahre', icon: Calendar },
                { value: 'unbekannt', label: 'Ich weiß es nicht', icon: AlertCircle },
                { value: 'keine', label: 'Derzeit keine Heizung', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.heatingAge === option.value}
                  onClick={() => handleSelect('heatingAge', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wann wurde Ihr Haus gebaut?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: 'vor1960', label: 'Vor 1960', icon: Building2 },
                { value: '1960-1979', label: '1960 - 1979', icon: Building2 },
                { value: '1980-1994', label: '1980 - 1994', icon: Building2 },
                { value: '1995-2002', label: '1995 - 2002', icon: Building2 },
                { value: 'nach2002', label: 'Nach 2002', icon: Building2 }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.buildingYear === option.value}
                  onClick={() => handleSelect('buildingYear', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wie groß ist die zu beheizende Fläche?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: '0-100', label: '0 bis 100 qm', icon: Maximize2 },
                { value: '101-150', label: '101 bis 150 qm', icon: Maximize2 },
                { value: '151-200', label: '151 bis 200 qm', icon: Maximize2 },
                { value: '201-300', label: '201 bis 300 qm', icon: Maximize2 },
                { value: '301-400', label: '301 bis 400 qm', icon: Maximize2 },
                { value: '400+', label: 'Mehr als 400 qm', icon: Maximize2 },
                { value: 'unbekannt', label: 'Ich weiß es nicht', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.heatedArea === option.value}
                  onClick={() => handleSelect('heatedArea', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wie viele Personen leben im Haushalt?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: '1-2', label: '1 bis 2', icon: Users },
                { value: '3-4', label: '3 bis 4', icon: Users },
                { value: '5-6', label: '5 bis 6', icon: Users },
                { value: '7-8', label: '7 bis 8', icon: Users },
                { value: '8+', label: 'Mehr als 8', icon: Users }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.householdSize === option.value}
                  onClick={() => handleSelect('householdSize', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wird Trinkwasser mit der Heizung erwärmt?
            </h2>
            <p className="text-center text-[#1A3A2E]/70 mb-8">Klicken Sie auf Ihre Wahl!</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { value: 'ja', label: 'Ja', icon: Droplet },
                { value: 'nein', label: 'Nein, separat', icon: AlertCircle },
                { value: 'unsicher', label: 'Unsicher', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.waterHeating === option.value}
                  onClick={() => handleSelect('waterHeating', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wie werden Ihre Räume beheizt?
            </h2>
            <p className="text-center text-[#1A3A2E]/70 mb-8">Klicken Sie auf Ihre Wahl!</p>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                { value: 'heizkoerper', label: 'Heizkörper', icon: Thermometer },
                { value: 'fussbodenheizung', label: 'Fußbodenheizung', icon: Thermometer },
                { value: 'beides', label: 'Heizkörper und Fußbodenheizung', icon: Thermometer },
                { value: 'sonstiges', label: 'Sonstiges', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.heatingSystem === option.value}
                  onClick={() => handleSelect('heatingSystem', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Wann soll die neue Heizung installiert werden?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { value: 'sofort', label: 'Sofort', icon: Clock },
                { value: '1-3', label: 'In 1 bis 3 Monaten', icon: Clock },
                { value: '3-6', label: 'In 3 bis 6 Monaten', icon: Clock },
                { value: '6-12', label: 'In 6 bis 12 Monaten', icon: Clock },
                { value: '12+', label: 'In mehr als 12 Monaten', icon: Clock },
                { value: 'nicht-geplant', label: 'Heizungswechsel nicht geplant', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.installationTime === option.value}
                  onClick={() => handleSelect('installationTime', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#1A3A2E] text-center mb-8">
              Haben Sie Probleme mit Ihrer aktuellen Heizung?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { value: 'gut', label: 'Funktioniert noch gut', icon: CheckCircle },
                { value: 'einschraenkungen', label: 'Funktioniert mit Einschränkungen', icon: AlertCircle },
                { value: 'ausgefallen', label: 'Vollständig ausgefallen', icon: AlertCircle }
              ].map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  value={option.value}
                  selected={formData.heatingProblems === option.value}
                  onClick={() => handleSelect('heatingProblems', option.value)}
                />
              ))}
            </div>
          </div>
        );

      case 11:
        return (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1A3A2E] mb-4">
                Unser System berechnet Ihre Anfrage...
              </h2>
              <p className="text-xl text-[#1A3A2E]/70">
                Großartig, wir haben alle relevanten Informationen, um Sie individuell beraten zu können.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#1A3A2E] mb-2">Vorname</label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    placeholder="Vorname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A3A2E] mb-2">Nachname</label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    placeholder="Nachname"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A3A2E] mb-2">E-Mail-Adresse</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="E-Mail-Adresse"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A3A2E] mb-2">Telefon (für eventuelle Rückfragen)</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="Telefon"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A3A2E] mb-2">PLZ</label>
                <Input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  required
                  placeholder="PLZ"
                />
              </div>
              <div className="flex items-start space-x-2">
                <input type="checkbox" required className="mt-1" />
                <p className="text-sm text-[#1A3A2E]/70">
                  Ich bin mit den Datenschutzbestimmungen einverstanden.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1A3A2E] text-white hover:bg-[#2D4A3E] py-6 text-lg"
              >
                Jetzt gratis Angebot erhalten
              </Button>
              <p className="text-center text-sm text-[#1A3A2E]/60">
                🔐 Sichere Datenübertragung - Ihre Daten werden rein zur Beratungszwecken genutzt und nicht an Dritte weitergegeben.
              </p>
            </form>
          </div>
        );

      case 13:
        return (
          <div className="text-center space-y-8">
            <div className="mb-12">
              <CheckCircle className="w-24 h-24 mx-auto text-green-600 mb-6" />
              <h2 className="text-5xl font-bold text-[#1A3A2E] mb-4">
                Vielen Dank für Ihre Anfrage!
              </h2>
              <p className="text-xl text-[#1A3A2E]/70">
                Ihre Anfrage wurde soeben eingesendet und ist erfolgreich bei uns eingegangen.
              </p>
            </div>
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-12">
                <h3 className="text-2xl font-bold text-[#1A3A2E] mb-8">Nächste Schritte</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#1A3A2E] flex items-center justify-center flex-shrink-0 text-white font-bold">
                      1
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-[#1A3A2E]">Anfrage</p>
                      <p className="text-[#1A3A2E]/70">Ihre Anfrage ist bei uns eingegangen</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#1A3A2E] flex items-center justify-center flex-shrink-0 text-white font-bold">
                      2
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-[#1A3A2E]">Erstkontakt in 1-2 Tagen</p>
                      <p className="text-[#1A3A2E]/70">Wir melden uns zeitnah bei Ihnen</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#1A3A2E] flex items-center justify-center flex-shrink-0 text-white font-bold">
                      3
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-[#1A3A2E]">Beratung</p>
                      <p className="text-[#1A3A2E]/70">Wir beraten Sie individuell und erstellen ein Angebot</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#1A3A2E] text-white hover:bg-[#2D4A3E] px-8 py-6 text-lg"
            >
              Zurück zur Startseite
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <div className="bg-[#1A3A2E] text-white py-6">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <div className="bg-white rounded-lg p-3">
                <img 
                  src="/novatus-logo.png" 
                  alt="Novatus Energie" 
                  className="h-20 w-auto"
                />
              </div>
            </a>
            <div className="text-right">
              <p className="text-sm text-[#E8F5E0]">📞 {mockData.company.phone}</p>
              <p className="text-sm text-[#E8F5E0]">✉️ {mockData.company.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {step <= totalSteps && (
          <div className="mb-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#1A3A2E]/70">Schritt {step} von {totalSteps}</p>
                <p className="text-sm text-[#1A3A2E]/70">{Math.round((step / totalSteps) * 100)}%</p>
              </div>
              <div className="w-full bg-[#1A3A2E]/20 rounded-full h-3">
                <div
                  className="bg-[#1A3A2E] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(step / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto">
          {step === 1 && (
            <div className="text-center mb-12">
              <h1 className="text-5xl md:text-6xl font-bold text-[#1A3A2E] mb-4">
                Jetzt Wärmepumpen-Angebot sichern
              </h1>
              <p className="text-xl text-[#1A3A2E]/70">
                In weniger als 2 Minuten eine gratis Beratung & ein individuelles Angebot sichern!
              </p>
            </div>
          )}

          {renderStep()}

          {step > 1 && step <= 10 && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={prevStep}
                variant="outline"
                className="border-2 border-[#1A3A2E] text-[#1A3A2E] hover:bg-[#1A3A2E] hover:text-white px-8 py-6 text-lg"
              >
                <ArrowLeft className="mr-2" /> Zurück
              </Button>
            </div>
          )}
        </div>

        <div className="mt-20">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center opacity-70">
            {mockData.partners.map((partner, idx) => (
              <div key={idx} className="flex items-center justify-center p-2 rounded-lg h-52">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-full w-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatPumpRequest;